import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { ExposureTemplateStatus, ExposureTemplateSummary } from '../exposure-register.models';
import { FlexiExposureTemplateService } from './exposure-template.service';

/** One status chip above the table. `null` is "All". */
interface StatusFilter {
  value: ExposureTemplateStatus | null;
  labelKey: string;
}

/**
 * Admin mode › Flexi › Settings › Exposure assessment templates.
 *
 * Every template of the application, whatever its status, is loaded once and
 * filtered here, so the chips can show their counts. The backend scopes the
 * list to the admin's application.
 */
@Component({
  selector: 'flexi-exposure-template-library',
  templateUrl: './exposure-template-library.component.html',
  // Same layout as the safety-round library, so its stylesheet is reused as is.
  styleUrls: ['../../safety-rounds/templates/safety-round-template-library.component.scss'],
  standalone: false,
})
export class ExposureTemplateLibraryComponent implements OnInit, OnDestroy {
  readonly TemplateStatus = ExposureTemplateStatus;

  readonly filters: StatusFilter[] = [
    { value: ExposureTemplateStatus.Published, labelKey: 'FLEXI_EXPOSURE.TEMPLATE_STATUS.1' },
    { value: ExposureTemplateStatus.Draft, labelKey: 'FLEXI_EXPOSURE.TEMPLATE_STATUS.0' },
    { value: ExposureTemplateStatus.Archived, labelKey: 'FLEXI_EXPOSURE.TEMPLATE_STATUS.2' },
    { value: null, labelKey: 'FLEXI_EXPOSURE.LIBRARY.FILTER_ALL' },
  ];

  templates: ExposureTemplateSummary[] = [];
  visible: ExposureTemplateSummary[] = [];
  loading = true;
  loadFailed = false;
  status: ExposureTemplateStatus | null = ExposureTemplateStatus.Published;
  search = '';
  /** Templates whose default switch is being saved. */
  busyIds = new Set<number>();

  /* ---- "New template" dialog ---- */
  createOpen = false;
  newName = '';
  newDescription = '';
  creating = false;

  /** The template whose assign drawer is open. */
  assigning: ExposureTemplateSummary | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: FlexiExposureTemplateService,
    private router: Router,
    private route: ActivatedRoute,
    private notifications: NotificationService,
  ) {}

  /** Published templates new businesses receive. */
  get defaultsForNew(): ExposureTemplateSummary[] {
    return this.templates.filter(t => t.IsDefaultForNewBusinesses && t.Status === ExposureTemplateStatus.Published);
  }

  get defaultsForNewNames(): string {
    return this.defaultsForNew.map(t => t.Name).join(', ');
  }

  get drafts(): ExposureTemplateSummary[] {
    return this.templates.filter(t => t.Status === ExposureTemplateStatus.Draft);
  }

  get draftNames(): string {
    return this.drafts.map(t => t.Name).join(', ');
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(): void {
    this.loading = true;
    this.loadFailed = false;
    this.api
      .list()
      .pipe(takeUntil(this.destroy$), finalize(() => (this.loading = false)))
      .subscribe({
        next: rows => {
          this.templates = rows;
          this.applyFilter();
        },
        error: () => {
          this.templates = [];
          this.visible = [];
          this.loadFailed = true;
        },
      });
  }

  count(status: ExposureTemplateStatus | null): number {
    return status === null ? this.templates.length : this.templates.filter(t => t.Status === status).length;
  }

  pickStatus(status: ExposureTemplateStatus | null): void {
    this.status = status;
    this.applyFilter();
  }

  /** Rebuilt rather than derived in a getter, so p-table keeps its paginator. */
  applyFilter(): void {
    const needle = this.search.trim().toLowerCase();
    this.visible = this.templates.filter(t =>
      (this.status === null || t.Status === this.status)
      && (!needle || t.Name.toLowerCase().includes(needle) || (t.Description ?? '').toLowerCase().includes(needle)));
  }

  open(template: ExposureTemplateSummary): void {
    this.router.navigate([template.Id], { relativeTo: this.route });
  }

  statusKey(status: ExposureTemplateStatus): string {
    return `FLEXI_EXPOSURE.TEMPLATE_STATUS.${status}`;
  }

  statusClass(status: ExposureTemplateStatus): string {
    switch (status) {
      case ExposureTemplateStatus.Published:
        return 'fvr-pill--done';
      case ExposureTemplateStatus.Draft:
        return 'fvr-pill--draft';
      default:
        return '';
    }
  }

  isPublished(template: ExposureTemplateSummary): boolean {
    return template.Status === ExposureTemplateStatus.Published;
  }

  /** Optimistic; the switch flips back if the save fails. */
  toggleDefault(template: ExposureTemplateSummary): void {
    if (!this.isPublished(template) || this.busyIds.has(template.Id)) {
      return;
    }
    const next = !template.IsDefaultForNewBusinesses;
    template.IsDefaultForNewBusinesses = next;
    this.busyIds.add(template.Id);
    this.api
      .setDefaultForNew(template.Id, next)
      .pipe(takeUntil(this.destroy$), finalize(() => this.busyIds.delete(template.Id)))
      .subscribe({
        next: () => this.notifications.success('UI_NOTIFICATIONS.SAVED'),
        error: () => (template.IsDefaultForNewBusinesses = !next),
      });
  }

  openAssign(template: ExposureTemplateSummary): void {
    this.assigning = template;
  }

  assignClosed(saved: boolean): void {
    this.assigning = null;
    if (saved) {
      this.load();
    }
  }

  /* ---- New template ---- */

  openCreate(): void {
    this.newName = '';
    this.newDescription = '';
    this.createOpen = true;
  }

  closeCreate(): void {
    if (!this.creating) {
      this.createOpen = false;
    }
  }

  create(): void {
    const name = this.newName.trim();
    if (!name || this.creating) {
      return;
    }
    this.creating = true;
    this.api
      .create({ Name: name, Description: this.newDescription.trim() || null })
      .pipe(takeUntil(this.destroy$), finalize(() => (this.creating = false)))
      .subscribe(id => {
        this.createOpen = false;
        this.router.navigate([id], { relativeTo: this.route });
      });
  }
}
